/**
 * DevicePairing.js
 *
 * UI for the device pairing flow:
 *  1. Select one of the user's registered FHIR devices
 *  2. Generate a 6-digit pairing code (shown as QR + numeric code)
 *  3. Enter device location so geofencing can work
 *  4. Activate the session once the device confirms
 *  5. Monitor geofence — auto-unpair when user leaves radius
 *  6. Manual unpair button
 *
 * Firebase Realtime DB paths used (also read by the physical device firmware):
 *   pairings/{deviceId}/offer    — one-time code offer (device reads this)
 *   pairings/{deviceId}/session  — active session record (device listens here)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CFormSelect,
  CFormInput,
  CFormLabel,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CAlert,
  CBadge,
  CProgress,
} from '@coreui/react';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import {
  generatePairingCode,
  writePairingOffer,
  activatePairingSession,
  revokePairingSession,
  subscribePairingSession,
  startGeofencing,
  PAIRING_CODE_TTL_MS,
  GEOFENCE_RADIUS_METERS,
} from './DevicePairingService';

// ─── QR Code (lightweight inline SVG approach via a data-uri canvas) ─────────
// We use a simple external-free ASCII fallback because the project may not have
// a QR library installed.  If `qrcode` npm package is later added, swap this out.
function QRPlaceholder({ value }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: 12,
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 8,
        width: 160,
      }}
    >
      {/* Simple visual stand-in until a real QR library is wired up */}
      <div
        style={{
          width: 120,
          height: 120,
          background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'%3E%3Crect width='10' height='10' fill='%23fff'/%3E%3Crect x='1' y='1' width='3' height='3' fill='%23000'/%3E%3Crect x='6' y='1' width='3' height='3' fill='%23000'/%3E%3Crect x='1' y='6' width='3' height='3' fill='%23000'/%3E%3Crect x='4' y='4' width='1' height='1' fill='%23000'/%3E%3Crect x='6' y='5' width='1' height='2' fill='%23000'/%3E%3Crect x='8' y='6' width='1' height='1' fill='%23000'/%3E%3Crect x='5' y='7' width='2' height='1' fill='%23000'/%3E%3C/svg%3E")`,
          backgroundSize: 'cover',
          imageRendering: 'pixelated',
        }}
      />
      <span style={{ fontSize: 10, color: '#888', wordBreak: 'break-all', textAlign: 'center' }}>
        {value}
      </span>
    </div>
  );
}

// ─── Pairing Code Countdown Timer ────────────────────────────────────────────
function PairingCodeTimer({ expiresAt, onExpire }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, expiresAt - Date.now());
      setRemaining(ms);
      if (ms === 0) onExpire?.();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpire]);

  const seconds = Math.ceil(remaining / 1000);
  const percent = Math.round((remaining / PAIRING_CODE_TTL_MS) * 100);

  return (
    <div style={{ marginTop: 8 }}>
      <small className="mb-2">Code expires in {seconds}s</small>
      <CProgress value={percent} color={percent > 30 ? 'success' : 'danger'} style={{ height: 4 }} />
    </div>
  );
}

// ─── Geofence Status Badge ────────────────────────────────────────────────────
function GeofenceBadge({ geoState }) {
  if (!geoState) return null;
  const { inRange, distance } = geoState;
  return (
    <CBadge color={inRange ? 'success' : 'warning'} className="ms-2">
      {inRange
        ? `In range (${Math.round(distance)}m)`
        : `Out of range (${Math.round(distance)}m)`}
    </CBadge>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const DevicePairing = ({ authUser, fhirToken }) => {
  // Device list
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  // Selected device for pairing
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  // Active pairing session state
  const [pairedDeviceId, setPairedDeviceId] = useState(null);
  const [pairingSession, setPairingSession] = useState(null);

  // Pairing code flow
  const [pairingCode, setPairingCode] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Geofence
  const [deviceLocation, setDeviceLocation] = useState({ lat: '', lon: '' });
  const [geoState, setGeoState] = useState(null);
  const stopGeofencingRef = useRef(null);

  // Modal for location entry
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Session listener cleanup
  const unsubscribeSessionRef = useRef(null);

  // ── Fetch devices from FHIR ─────────────────────────────────────────────
  const fetchDevices = useCallback(async () => {
    if (!authUser?.uid) return;
    try {
      const idToken = await authUser.getValidToken?.();
      const response = await axios.get(
        `${config.fhirUrl}/fhir/Device?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const entries = response.data.entry || [];
      setDevices(
        entries.map((e) => ({
          id: e.resource.id,
          label: e.resource.type?.coding?.[0]?.display || e.resource.id,
          status: e.resource.status,
        }))
      );
    } catch (err) {
      console.error('DevicePairing: error fetching devices', err);
    } finally {
      setDevicesLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      unsubscribeSessionRef.current?.();
      stopGeofencingRef.current?.();
    };
  }, []);

  // ── Generate pairing code ───────────────────────────────────────────────
  const handleGenerateCode = async () => {
    if (!selectedDeviceId) {
      toast.warning('Please select a device first.');
      return;
    }
    if (!authUser?.uid) {
      toast.error('Not authenticated.');
      return;
    }

    setIsGenerating(true);
    try {
      const code = generatePairingCode();
      const expiresAt = Date.now() + PAIRING_CODE_TTL_MS;

      await writePairingOffer(selectedDeviceId, authUser.uid, code);

      setPairingCode(code);
      setCodeExpiresAt(expiresAt);
      toast.success('Pairing code generated. Enter it on your device.');
    } catch (err) {
      console.error('DevicePairing: error writing offer', err);
      toast.error('Failed to generate pairing code.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Handle code expiry ──────────────────────────────────────────────────
  const handleCodeExpired = () => {
    setPairingCode(null);
    setCodeExpiresAt(null);
    toast.warning('Pairing code expired. Generate a new one.');
  };

  // ── Manually activate session (for web-side confirmation/testing) ───────
  // In production the device firmware writes to pairings/{deviceId}/session
  // after it validates the code with your backend. This button lets the web app
  // simulate that step or bypass it for testing.
  const handleActivateSession = async () => {
    if (!selectedDeviceId || !authUser?.uid) return;
    try {
      await activatePairingSession(selectedDeviceId, authUser.uid);
      startPairingSession(selectedDeviceId);
      setPairingCode(null);
      setCodeExpiresAt(null);
      toast.success('Session activated! Device is now paired.');
    } catch (err) {
      console.error('DevicePairing: error activating session', err);
      toast.error('Failed to activate pairing session.');
    }
  };

  // ── Subscribe to session + start geofencing ─────────────────────────────
  const startPairingSession = (deviceId) => {
    // Clean up any previous session listener
    unsubscribeSessionRef.current?.();
    stopGeofencingRef.current?.();

    setPairedDeviceId(deviceId);

    // Listen for remote revocation (e.g., device signals it wants to unpair)
    const unsub = subscribePairingSession(deviceId, (session) => {
      setPairingSession(session);
      if (session && session.active === false) {
        handleSessionRevoked('remote');
      }
    });
    unsubscribeSessionRef.current = unsub;

    // Prompt for device location to enable geofencing
    setShowLocationModal(true);
  };

  // ── Start geofencing after location is provided ─────────────────────────
  const handleStartGeofence = () => {
    const lat = parseFloat(deviceLocation.lat);
    const lon = parseFloat(deviceLocation.lon);

    if (isNaN(lat) || isNaN(lon)) {
      toast.warning('Please enter valid coordinates.');
      return;
    }

    setShowLocationModal(false);

    const stop = startGeofencing({
      deviceId: pairedDeviceId,
      deviceLat: lat,
      deviceLon: lon,
      onOutOfRange: ({ distance }) => {
        toast.warning(
          `Auto-unpaired: you moved ${Math.round(distance)}m from the device (limit: ${GEOFENCE_RADIUS_METERS}m).`
        );
        handleSessionRevoked('geofence');
      },
      onLocationUpdate: (update) => {
        setGeoState(update);
      },
    });

    stopGeofencingRef.current = stop;
    toast.info(`Geofencing active — ${GEOFENCE_RADIUS_METERS}m radius.`);
  };

  // Skip geofencing if location isn't available/needed
  const handleSkipGeofence = () => {
    setShowLocationModal(false);
    toast.info('Geofencing skipped. You can unpair manually.');
  };

  // ── Use browser location for device coords ──────────────────────────────
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDeviceLocation({
          lat: pos.coords.latitude.toFixed(6),
          lon: pos.coords.longitude.toFixed(6),
        });
        toast.success('Location captured.');
      },
      (err) => {
        toast.error(`Could not get location: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  };

  // ── Unpair (manual or auto) ─────────────────────────────────────────────
  const handleUnpair = async (reason = 'user_initiated') => {
    if (!pairedDeviceId) return;
    try {
      await revokePairingSession(pairedDeviceId, reason);
      handleSessionRevoked(reason);
      toast.success('Device unpaired.');
    } catch (err) {
      console.error('DevicePairing: error revoking session', err);
      toast.error('Failed to unpair device.');
    }
  };

  const handleSessionRevoked = (reason) => {
    stopGeofencingRef.current?.();
    stopGeofencingRef.current = null;
    unsubscribeSessionRef.current?.();
    unsubscribeSessionRef.current = null;
    setPairedDeviceId(null);
    setPairingSession(null);
    setGeoState(null);

    if (reason === 'geofence') {
      toast.warning('Auto-unpaired: left geofence radius.');
    } else if (reason === 'remote') {
      toast.info('Device has ended the pairing session.');
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  const selectedDevice = devices.find((d) => d.id === selectedDeviceId);
  const isPaired = !!pairedDeviceId;

  return (
    <>
      <CCard className="shadow">
        <CCardHeader className="text-center text-primary">
          <h4>Device Pairing</h4>
          <small className="mb-2">
            Pair your physical device with this account for real-time preference sync
          </small>
        </CCardHeader>
        <CCardBody>
          {/* ── Currently Paired Status ── */}
          {isPaired && (
            <CAlert color="success" className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Paired:</strong>{' '}
                {devices.find((d) => d.id === pairedDeviceId)?.label || pairedDeviceId}
                <GeofenceBadge geoState={geoState} />
              </div>
              <CButton color="danger" size="sm" onClick={() => handleUnpair('user_initiated')}>
                Unpair
              </CButton>
            </CAlert>
          )}

          {/* ── Device Selector ── */}
          {!isPaired && (
            <>
              <div className="mb-3">
                <CFormLabel>Select a registered device to pair</CFormLabel>
                {devicesLoading ? (
                  <div className="d-flex align-items-center gap-2">
                    <CSpinner size="sm" />
                    <small>Loading devices…</small>
                  </div>
                ) : devices.length === 0 ? (
                  <CAlert color="warning">
                    No devices registered. Add a device in API Key Management above.
                  </CAlert>
                ) : (
                  <CFormSelect
                    value={selectedDeviceId}
                    onChange={(e) => {
                      setSelectedDeviceId(e.target.value);
                      setPairingCode(null);
                      setCodeExpiresAt(null);
                    }}
                  >
                    <option value="">— choose device —</option>
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.label} ({d.status})
                      </option>
                    ))}
                  </CFormSelect>
                )}
              </div>

              {/* ── Generate Code ── */}
              {selectedDeviceId && !pairingCode && (
                <CButton
                  color="primary"
                  onClick={handleGenerateCode}
                  disabled={isGenerating}
                  className="mb-3"
                >
                  {isGenerating ? (
                    <>
                      <CSpinner size="sm" className="me-1" /> Generating…
                    </>
                  ) : (
                    'Generate Pairing Code'
                  )}
                </CButton>
              )}

              {/* ── Code Display ── */}
              {pairingCode && (
                <div className="pairing-code-display d-flex flex-column align-items-center gap-3 py-3">
                  <QRPlaceholder
                    value={`mieros://pair?device=${selectedDeviceId}&code=${pairingCode}`}
                  />

                  <div className="text-center">
                    <div
                      style={{
                        fontSize: 36,
                        fontWeight: 700,
                        letterSpacing: 8,
                        fontFamily: 'monospace',
                        color: '#2d7be5',
                      }}
                    >
                      {pairingCode}
                    </div>
                    <small className="mb-2">
                      Enter this code on your device, or scan the QR code
                    </small>
                    <PairingCodeTimer
                      expiresAt={codeExpiresAt}
                      onExpire={handleCodeExpired}
                    />
                  </div>

                  <div className="d-flex gap-2 flex-wrap justify-content-center">
                    <CButton
                      color="success"
                      onClick={handleActivateSession}
                      title="Use this to confirm pairing from the web side (testing / BLE-less flow)"
                    >
                      Confirm Pairing
                    </CButton>
                    <CButton
                      color="secondary"
                      variant="outline"
                      onClick={handleGenerateCode}
                      disabled={isGenerating}
                    >
                      Regenerate Code
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      onClick={() => {
                        setPairingCode(null);
                        setCodeExpiresAt(null);
                      }}
                    >
                      Cancel
                    </CButton>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Geofence Status (while paired) ── */}
          {isPaired && geoState && (
            <div className="mt-3 p-3 border rounded">
              <strong>Geofence</strong>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span
                  className={`status-dot ${geoState.inRange ? 'bg-success' : 'bg-warning'}`}
                  style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block' }}
                />
                <span>
                  Distance to device: <strong>{Math.round(geoState.distance)} m</strong>
                  {' '}&nbsp;/ Limit: {GEOFENCE_RADIUS_METERS} m
                </span>
              </div>
              <CProgress
                value={Math.min(100, (geoState.distance / GEOFENCE_RADIUS_METERS) * 100)}
                color={geoState.inRange ? 'success' : 'danger'}
                className="mt-2"
                style={{ height: 6 }}
              />
            </div>
          )}

          {/* ── Session debug info ── */}
          {isPaired && pairingSession && (
            <div className="mt-3 mb-2 small">
              Session active since:{' '}
              {pairingSession.pairedAt
                ? new Date(pairingSession.pairedAt).toLocaleTimeString()
                : 'N/A'}
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* ── Location Modal ── */}
      <CModal visible={showLocationModal} onClose={handleSkipGeofence}>
        <CModalHeader>
          <CModalTitle>Set Device Location for Geofencing</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-2 small mb-3">
            Provide the physical location of your device so the app can automatically unpair when
            you move more than <strong>{GEOFENCE_RADIUS_METERS} metres</strong> away.
          </p>
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            className="mb-3"
            onClick={handleUseMyLocation}
          >
            Use My Current Location
          </CButton>
          <div className="d-flex gap-2">
            <div className="flex-fill">
              <CFormLabel>Latitude</CFormLabel>
              <CFormInput
                type="number"
                step="any"
                placeholder="e.g. 37.7749"
                value={deviceLocation.lat}
                onChange={(e) => setDeviceLocation((p) => ({ ...p, lat: e.target.value }))}
              />
            </div>
            <div className="flex-fill">
              <CFormLabel>Longitude</CFormLabel>
              <CFormInput
                type="number"
                step="any"
                placeholder="e.g. -122.4194"
                value={deviceLocation.lon}
                onChange={(e) => setDeviceLocation((p) => ({ ...p, lon: e.target.value }))}
              />
            </div>
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleSkipGeofence}>
            Skip (no geofencing)
          </CButton>
          <CButton color="primary" onClick={handleStartGeofence}>
            Enable Geofencing
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default DevicePairing;


